import React from "react";
import {
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";
import { format, isValid } from "date-fns";

// Helper fecha
function normalizeDate(input) {
  if (!input) return null;
  if (typeof input === "object" && "seconds" in input) {
    const d = new Date(input.seconds * 1000);
    return isValid(d) ? d : null;
  }
  if (typeof input === "number") {
    const ms = input < 1e12 ? input * 1000 : input;
    const d = new Date(ms);
    return isValid(d) ? d : null;
  }
  if (typeof input === "string") {
    const d = new Date(input);
    return isValid(d) ? d : null;
  }
  return null;
}

// Estilos compactos
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFF",
    padding: 8,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  headerLeft: { flexDirection: "column", gap: 2, maxWidth: "75%" },
  headerRight: { alignItems: "flex-end" },
  headerTitle: { fontSize: 9, fontWeight: 700 },
  text: { fontSize: 6, lineHeight: 1.1 },
  text2: { fontSize: 6.5, lineHeight: 1.1, marginTop: 2 },
  img: { width: 80 },
  inlineView: {
    flexDirection: "row",
    gap: 6,
    marginTop: 4,
  },
  inlineBox: {
    flex: 1,
    borderTopWidth: 0.5,
    borderTopColor: "#000",
    paddingTop: 2,
  },
  table: {
    width: "100%",
    marginTop: 6,
    borderWidth: 0.5,
    borderColor: "#000",
    borderRadius: 2,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f3f3f3",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 0.25,
    borderBottomColor: "#999",
  },
  colCodigo: { width: "28%", padding: 2 },
  colZona: { width: "14%", padding: 2 },
  colCliente: { width: "18%", padding: 2 },
  colFecha: { width: "14%", padding: 2 },
  colDireccion: { width: "26%", padding: 2 },
  th: { fontSize: 6.2, lineHeight: 1.1, fontWeight: 700 },
  td: { fontSize: 6, lineHeight: 1.05 },
  footer: { marginTop: 4, fontSize: 6, textAlign: "right" },
});

// util para dividir en páginas de 60
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// util para dividir en páginas con primer límite distinto
function paginateWithFirst(arr, firstSize, otherSize) {
  const pages = [];
  if (arr.length <= firstSize) {
    pages.push(arr);
    return pages;
  }
  // primera página con "firstSize"
  pages.push(arr.slice(0, firstSize));
  let i = firstSize;
  while (i < arr.length) {
    pages.push(arr.slice(i, i + otherSize));
    i += otherSize;
  }
  return pages;
}

const CreateManiFestByCurrentListPdf = ({ orders = [] }) => {
  const hasOrders = Array.isArray(orders) && orders.length > 0;
  const driverName = hasOrders ? orders[0]?.driverName ?? "" : "";
  const nowStr = format(new Date(), "dd/MM/yy HH:mm:ss");

  const FIRST_PAGE = 42; // 👉 primera hoja máximo 42 filas
  const OTHER_PAGES = 60; // 👉 resto hasta 60
  const pages = hasOrders
    ? paginateWithFirst(orders, FIRST_PAGE, OTHER_PAGES)
    : [[]];

  return (
    <Document>
      {pages.map((ordersPage, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <Text style={styles.headerTitle}>BICCI Hoja de Ruta</Text>
              <Text style={styles.text}>
                Guía de despachos a retirar en bodega
              </Text>
              <Text style={styles.text2}>
                FOLIOS#: {hasOrders ? orders.length : 0} | Fecha: {nowStr} |
                Pág. {pageIndex + 1} / {pages.length}
              </Text>
              {hasOrders && (
                <Text style={styles.text}>
                  Asignado a: {driverName}. Recuerda registrar evidencias en la
                  app BICCI RIDER.
                </Text>
              )}
            </View>
            <View style={styles.headerRight}>
              <Image
                src={"/static/images/bicciexpress.png"}
                style={styles.img}
              />
            </View>
          </View>

          <View style={styles.inlineView}>
            <View style={styles.inlineBox}>
              <Text style={styles.text}>OPERACIONES</Text>
            </View>
            <View style={styles.inlineBox}>
              <Text style={styles.text}>CONDUCTOR: {driverName}</Text>
            </View>
          </View>

          {hasOrders ? (
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <View style={styles.colCodigo}>
                  <Text style={styles.th}>CÓDIGO / LPN</Text>
                </View>
                <View style={styles.colZona}>
                  <Text style={styles.th}>ZONA</Text>
                </View>
                <View style={styles.colCliente}>
                  <Text style={styles.th}>CLIENTE</Text>
                </View>
                <View style={styles.colFecha}>
                  <Text style={styles.th}>FECHA</Text>
                </View>
                <View style={styles.colDireccion}>
                  <Text style={styles.th}>DIRECCIÓN</Text>
                </View>
              </View>

              {ordersPage.map((element, index) => (
                <View key={index} style={styles.row}>
                  <View style={styles.colCodigo}>
                    <Text style={styles.td}>{element?.id ?? ""}</Text>
                    <Text style={styles.td}>LPN: {element?.lpn ?? ""}</Text>
                  </View>
                  <View style={styles.colZona}>
                    <Text style={styles.td}>{element?.commune2 ?? ""}</Text>
                  </View>
                  <View style={styles.colCliente}>
                    <Text style={styles.td}>
                      {(element?.customer_name || "").toString().toLowerCase()}
                    </Text>
                  </View>
                  <View style={styles.colFecha}>
                    <Text style={styles.td}>{nowStr}</Text>
                  </View>
                  <View style={styles.colDireccion}>
                    <Text style={styles.td}>{element?.address ?? ""}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={{ fontSize: 8, marginTop: 6 }}>
              No hay órdenes para generar el PDF.
            </Text>
          )}

          <Text style={styles.footer}>Generado: {nowStr} • BICCI</Text>
        </Page>
      ))}
    </Document>
  );
};

export default CreateManiFestByCurrentListPdf;
